/**
 * Newsletter Pulse Report Templates
 * Based on best practices from Mailchimp, ConvertKit, Substack
 */

import { PulseReportTemplate } from "./types";

export const PRODUCT_NEWSLETTER: PulseReportTemplate = {
  id: "product-newsletter",
  name: "Product Newsletter",
  reportType: "newsletter",
  description: "Share product news, tips, and best practices",
  purpose:
    "Keep users engaged with valuable content about using your product effectively.",
  icon: "BulbOutlined",
  color: "#10b981",
  suggestedHtmlTemplateId: "newsletter",
  suggestedSubject: "{{newsletter_title}} - Tips & Updates",
  defaultSchedule: {
    type: "weekly",
    dayOfWeek: 2,
    time: "10:00",
  },
  defaultDataSections: {
    billing: false,
    usage: false,
    performance: false,
    analytics: false,
    team: false,
  },
  targetAudience: "All users interested in product updates",
  expectedOutcomes: [
    "Increased feature adoption",
    "Stronger brand relationship",
    "Reduced churn through education",
    "Community building",
  ],
  bestPractices: [
    "Provide genuine value, not just promotions",
    "Use storytelling and real examples",
    "Include actionable tips",
    "Keep a consistent schedule",
  ],
  keyMetrics: [
    "Open rate",
    "Click-through rate",
    "Feature adoption",
    "Engagement score",
  ],
  tags: ["newsletter", "content", "education", "tips", "engagement"],
  bestFor: ["User education", "Feature adoption", "Brand building"],
  difficulty: "intermediate",
  setupTime: "15 minutes",
  previewDescription: "Educational content newsletter",
};

export const INDUSTRY_INSIGHTS: PulseReportTemplate = {
  id: "industry-insights",
  name: "Industry Insights Newsletter",
  reportType: "newsletter",
  description: "Curated industry news and trends",
  purpose:
    "Position your brand as a thought leader by sharing valuable industry insights.",
  icon: "GlobalOutlined",
  color: "#6366f1",
  suggestedHtmlTemplateId: "newsletter",
  suggestedSubject: "Industry Trends - {{current_month}} {{year}}",
  defaultSchedule: {
    type: "monthly",
    dayOfMonth: 15,
    time: "10:00",
  },
  defaultDataSections: {
    billing: false,
    usage: false,
    performance: false,
    analytics: false,
    team: false,
  },
  targetAudience: "Decision makers and industry professionals",
  expectedOutcomes: [
    "Thought leadership positioning",
    "Increased brand authority",
    "Higher engagement from executives",
    "Partnership opportunities",
  ],
  bestPractices: [
    "Curate genuinely valuable content",
    "Add your unique perspective",
    "Include data and statistics",
    "Make it shareable",
  ],
  keyMetrics: [
    "Open rate",
    "Forward rate",
    "Social shares",
    "Response rate",
  ],
  tags: ["newsletter", "industry", "insights", "trends", "thought-leadership"],
  bestFor: ["Thought leadership", "Brand authority", "Executive engagement"],
  difficulty: "advanced",
  setupTime: "20 minutes",
  previewDescription: "Industry insights and trends digest",
};

export const TIPS_AND_TRICKS: PulseReportTemplate = {
  id: "tips-and-tricks",
  name: "Tips & Tricks Newsletter",
  reportType: "newsletter",
  description: "Quick tips to get more from the platform",
  purpose:
    "Help users discover hidden features and optimize their workflow with actionable tips.",
  icon: "RocketOutlined",
  color: "#f59e0b",
  suggestedHtmlTemplateId: "newsletter",
  suggestedSubject: "Pro Tip: {{tip_title}}",
  defaultSchedule: {
    type: "weekly",
    dayOfWeek: 4,
    time: "11:00",
  },
  defaultDataSections: {
    billing: false,
    usage: false,
    performance: false,
    analytics: false,
    team: false,
  },
  targetAudience: "Active users looking to optimize",
  expectedOutcomes: [
    "Increased feature discovery",
    "Higher user satisfaction",
    "Reduced support burden",
    "Power user development",
  ],
  bestPractices: [
    "One tip per email for focus",
    "Include step-by-step instructions",
    "Add screenshots or GIFs",
    "Link to related resources",
  ],
  keyMetrics: [
    "Feature adoption rate",
    "Click-through rate",
    "Support ticket reduction",
    "User satisfaction",
  ],
  tags: ["newsletter", "tips", "tricks", "how-to", "education"],
  bestFor: ["Feature adoption", "User education", "Support deflection"],
  difficulty: "beginner",
  setupTime: "10 minutes",
  previewDescription: "Quick actionable tips and tricks",
};

export const COMMUNITY_SPOTLIGHT: PulseReportTemplate = {
  id: "community-spotlight",
  name: "Community Spotlight",
  reportType: "newsletter",
  description: "Highlight community success stories and achievements",
  purpose:
    "Build community by showcasing user success stories and best practices from peers.",
  icon: "TeamOutlined",
  color: "#ec4899",
  suggestedHtmlTemplateId: "newsletter",
  suggestedSubject: "Community Spotlight: {{featured_user}} Success Story",
  defaultSchedule: {
    type: "monthly",
    dayOfMonth: 1,
    time: "10:00",
  },
  defaultDataSections: {
    billing: false,
    usage: false,
    performance: false,
    analytics: false,
    team: false,
  },
  targetAudience: "Engaged community members",
  expectedOutcomes: [
    "Stronger community bonds",
    "Social proof for prospects",
    "User-generated content",
    "Increased advocacy",
  ],
  bestPractices: [
    "Feature diverse user stories",
    "Include specific results and metrics",
    "Make heroes of your users",
    "Encourage submissions",
  ],
  keyMetrics: [
    "Story submissions",
    "Social shares",
    "Community engagement",
    "NPS improvement",
  ],
  tags: ["newsletter", "community", "spotlight", "success-stories", "social-proof"],
  bestFor: ["Community building", "Social proof", "User advocacy"],
  difficulty: "intermediate",
  setupTime: "15 minutes",
  previewDescription: "User success stories and community highlights",
};

export const NEWSLETTER_TEMPLATES = [
  PRODUCT_NEWSLETTER,
  INDUSTRY_INSIGHTS,
  TIPS_AND_TRICKS,
  COMMUNITY_SPOTLIGHT,
];
